import React, { useState, useEffect } from "react";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { Button } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import { Typography, Grid } from "@mui/material";
import { RegisterStudent } from "../../utils/axios/registerandloginAPI";
import {
  validateEmail,
  validatePhoneNumber,
  validateName,
  validateCollegeName,
  validateBranchName,
} from "../../utils/validate";
import CustomButton from "../Common/Button";
import Logo from "../Common/Logo";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

const Register = () => {
  const navigate = useNavigate();

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [collegeName, setCollegeName] = useState("");
  const [branch, setBranch] = useState("");

  // Error states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [collegeNameError, setCollegeNameError] = useState("");
  const [branchError, setBranchError] = useState("");
  const [formError, setFormError] = useState("");

  const [open, setOpen] = useState(false);

  // Dialog
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Reusable validator
  const updateError = (value, validateFunc, setErrorFunc, errorMessage) => {
    if (!value.trim()) {
      setErrorFunc("");
      return;
    }
    if (!validateFunc(value)) {
      setErrorFunc(errorMessage);
    } else {
      setErrorFunc("");
    }
  };

  // Live validation
  useEffect(() => {
    updateError(
      firstName,
      validateName,
      setFirstNameError,
      "Please enter a valid first name (letters only)."
    );
    updateError(
      lastName,
      validateName,
      setLastNameError,
      "Please enter a valid last name (letters only)."
    );
    updateError(
      email,
      validateEmail,
      setEmailError,
      "Please enter a valid email address."
    );
    updateError(
      phoneNumber,
      validatePhoneNumber,
      setPhoneError,
      "Please enter a valid 10-digit phone number."
    );
    updateError(
      collegeName,
      validateCollegeName,
      setCollegeNameError,
      "Please enter a valid college name."
    );
    updateError(
      branch,
      validateBranchName,
      setBranchError,
      "Please enter a valid branch name."
    );
  }, [firstName, lastName, email, phoneNumber, collegeName, branch]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      firstNameError ||
      lastNameError ||
      emailError ||
      phoneError ||
      collegeNameError ||
      branchError
    ) {
      setFormError("Please fix the highlighted errors before submitting.");
      return;
    }

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !phoneNumber.trim() ||
      !collegeName.trim() ||
      !branch.trim()
    ) {
      setFormError("All fields are required.");
      return;
    }

    setFormError("");
    await registerStudent();
  };

  // Register API
  const registerStudent = async () => {
    let date = new Date();
    const payload = {
      RoleId: 2,
      FirstName: firstName.trim(),
      LastName: lastName.trim(),
      CollegeName: collegeName.trim(),
      PhoneNumber: phoneNumber.trim(),
      Year: date.getFullYear(),
      Department: branch.trim(),
      Email: email.trim().toLowerCase(),
    };

    let response = await RegisterStudent(payload);

    if (response.success) {
      handleClickOpen();
      setTimeout(() => handleClose(), 6000);
      setTimeout(() => navigate("/"), 3000);
    } else {
      setFormError("This email is already registered. Please login.");
    }
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const img = new Image();
    img.src = require("../../assets/images/Evalutech-Login.webp");
    img.onload = () => setIsLoading(false);
    img.onerror = () => setIsLoading(false);
  }, []);

  return (
    <>
      {isLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(255,255,255,1)", zIndex: 99999, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <img src={require("../../assets/images/Evalutech.png")} alt="Evalutech Logo" style={{ height: "6rem", marginBottom: "20px" }} />
          <h2 style={{ color: "#F59E0B" }}>Loading...</h2>
        </div>
      )}
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          width: "100vw",
          backgroundImage: `url(${require("../../assets/images/Evalutech-Login.webp")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          alignItems: "center",
          justifyContent: "flex-end",
          fontFamily: "'Poppins', sans-serif",
          position: "relative",
        }}
      >
        {/* Top Left Sunware Technologies Logo Box Wrapper for Shadow */}
        <div style={{ position: 'absolute', top: '1.1vw', left: 0, filter: 'drop-shadow(0px 4px 15px rgba(0,0,0,0.08))' }}>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '0.9vw 4.6vw 0.9vw 1.9vw', // extra right padding to compensate for the slant
              clipPath: 'polygon(0 0, 100% 0, 82% 100%, 0% 100%)', // Slanted right edge
              display: 'flex',
              alignItems: 'center',
              gap: '0.9vw',
            }}
          >
            <img
              src={require("../../assets/images/sunwareLogo.png")}
              alt="Sunware Logo"
              style={{ height: '2.73vw' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <span style={{ fontWeight: '800', color: '#1E293B', fontSize: '1.4vw', lineHeight: '1.1', letterSpacing: '0.04vw' }}>
                SUNWARE
              </span>
              <span style={{ fontWeight: '600', color: '#64748B', fontSize: '0.78vw', letterSpacing: '0.08vw' }}>
                TECHNOLOGIES
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "90%",
            maxWidth: "clamp(350px, 30vw, 500px)",
            maxHeight: "95vh",
            overflowY: "auto",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            padding: "clamp(20px, 2.5vw, 30px) clamp(25px, 3vw, 40px)",
            marginRight: "6%",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <div style={{ marginBottom: "clamp(8px, 1vw, 15px)" }}>
            <img src={require("../../assets/images/Evalutech.png")} alt="Evalutech Logo" style={{ height: "clamp(35px, 3.5vw, 55px)" }} />
          </div>

          <Typography style={{ color: "#F59E0B", fontSize: "clamp(16px, 1.8vw, 22px)", fontWeight: "700", marginBottom: "clamp(12px, 1.5vw, 20px)" }}>
            Ready to take the first step?
          </Typography>

          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 'clamp(8px, 1vw, 12px)' }}>
              <img src={require("../../assets/images/Animation - 1699989202315.gif")} alt="Register Icon" style={{ width: 'clamp(24px, 2.5vw, 35px)' }} />
              <Typography style={{ color: '#4F46E5', fontWeight: '700', fontSize: 'clamp(12px, 1vw, 15px)', letterSpacing: '1px' }}>
                REGISTER
              </Typography>
            </div>

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="First Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
                <TextField
                  placeholder="Last Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="Email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: <MailOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="Phone Number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  InputProps={{
                    startAdornment: <PhoneOutlinedIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="College Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={collegeName}
                  onChange={(e) => setCollegeName(e.target.value)}
                  InputProps={{
                    startAdornment: <AccountBalanceOutlinedIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              <div style={{ marginBottom: 'clamp(12px, 1.5vw, 18px)' }}>
                <TextField
                  placeholder="Branch"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  InputProps={{
                    startAdornment: <AutoStoriesOutlinedIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              {formError && (
                <Typography style={{ color: 'red', fontSize: 'clamp(11px, 0.9vw, 13px)', marginBottom: '10px', textAlign: 'center' }}>
                  {formError}
                </Typography>
              )}
              {firstNameError || lastNameError || emailError || phoneError || collegeNameError || branchError ? (
                <Typography style={{ color: 'red', fontSize: 'clamp(11px, 0.9vw, 13px)', marginBottom: '10px', textAlign: 'center' }}>
                  Please check the input fields.
                </Typography>
              ) : null}

              <Button
                type="submit"
                fullWidth
                style={{
                  backgroundColor: "#F59E0B",
                  color: "#fff",
                  borderRadius: "30px",
                  padding: "clamp(8px, 1vw, 12px)",
                  fontSize: "clamp(14px, 1.2vw, 17px)",
                  fontWeight: "600",
                  textTransform: "none",
                  display: "flex",
                  gap: "8px",
                  boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)'
                }}
              >
                Create Account <ArrowForwardIcon fontSize="clamp(15px, 1.3vw, 18px)" />
              </Button>
            </form>

            <Typography style={{ textAlign: "center", marginTop: "clamp(15px, 2vw, 25px)", fontSize: "clamp(12px, 1.1vw, 14px)", color: "#6B7280" }}>
              Already have an account?{" "}
              <Link to={"/login"} style={{ color: "#4F46E5", textDecoration: "none", fontWeight: "600" }}>
                Login
              </Link>
            </Typography>
          </div>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogContent>
            <DialogContentText>User successfully registered.</DialogContentText>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Register;
